import { memo, useEffect, useState } from 'react';
import { TableRowProps } from '../Table/TableRow';
import LegalTermsModal from 'components/atoms/LegalTermsModal/LegalTermsModal';

import { AnalyticsProps } from '../../../InternalLibrary';
import { CalculatorContext } from './CalculatorContext';
import { Form } from './Form';
import { LoanInfoButton } from './LoanInfoButton';
import { LoanSummary } from './LoanSummary';
import { SubmitButton } from './SubmitButton';

/**
 * Types
 */
export interface CalculatorData {
  amount: number;
  days: number;
}

export type Interval = {
  min?: number | undefined;
  max?: number | undefined;
  step?: number | undefined;
  defaultValue?: number | undefined;
};

export interface CalculatorProps extends AnalyticsProps {
  content: {
    title: string;
    amountTitle: string;
    amountUnit: string;
    daysTitle?: string;
    daysUnit?: string;
    confirmChanges: string;
    openLoanInformationModal?: string;
    swiperQuantity: string;
    swiperTerm: string;
  };
  constraints?: {
    amount: Interval;
    term: Interval;
  };
  data?: CalculatorData;
  onChange?: (key: string, value: number) => void;
  onSubmit?: (data: CalculatorData) => void | Promise<void>;
  tableRows?: TableRowProps[];
  hideDays?: boolean;
  children?: React.ReactNode;
}

type CalculatorComponent = React.MemoExoticComponent<
  React.FC<CalculatorProps>
> & {
  Form: typeof Form;
  LoanSummary: typeof LoanSummary;
  SubmitButton: typeof SubmitButton;
  LoanInfoButton: typeof LoanInfoButton;
};

/**
 * Components
 */
const Calculator = memo((props: CalculatorProps) => {
  const { content, data, children } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [amount, setAmount] = useState(() => data?.amount || 0);
  const [days, setDays] = useState(() => data?.days || 0);

  useEffect(() => {
    if (!data) return;

    setAmount(data.amount);
    setDays(data.days);
  }, [data?.amount]);

  const hasModal = Boolean(content.openLoanInformationModal);

  return (
    <CalculatorContext.Provider
      value={{
        ...props,
        hasModal,
        showModal,
        setShowModal,
        isLoading,
        setIsLoading,
        amount,
        setAmount,
        days,
        setDays,
      }}
    >
      {hasModal && showModal && (
        <LegalTermsModal
          onClose={() => setShowModal(false)}
          amount={amount}
          term={days}
        />
      )}
      {children}
    </CalculatorContext.Provider>
  );
}) as CalculatorComponent;

Calculator.Form = Form;
Calculator.LoanSummary = LoanSummary;
Calculator.SubmitButton = SubmitButton;
Calculator.LoanInfoButton = LoanInfoButton;

export { Calculator };
